import { AttachmentCategoryEnum, ModelsEnum } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useCallback, useState } from "react";

import { toast } from "@/components/toast/toast-queue";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import apiClient from "@/lib/api/client";

// Simplified unified file interface
export interface ManagedFile {
  // Results
  attachmentId?: number;
  errorMessage?: string;

  // Core properties
  file: File;
  id: string;

  // Metadata
  isExisting?: boolean; // For edit mode existing files
  // Upload state
  progress: number;

  status: "error" | "pending" | "success" | "uploading";
}

interface PresignedUrlResponse {
  attachmentId: number;
  fields: Record<string, string>;
  presignedUrl: string;
  url: string;
}

const MAX_FILES = 10;

export function useFileUpload(): {
  // Actions
  addExistingFiles: (
    existingFiles: Array<{
      attachmentId: number;
      file: File;
    }>,
  ) => void;
  addFiles: (newFiles: File[]) => Promise<void>;
  clearAll: () => void;
  failedUploads: ManagedFile[];
  // Computed
  files: ManagedFile[];
  hasFiles: boolean;

  isUploading: boolean;
  removeFile: (fileId: string) => void;
  retryUpload: (fileId: string) => Promise<void>;
  successfulUploads: ManagedFile[];
} {
  const [files, setFiles] = useState<ManagedFile[]>([]);

  const { data: request } = useGetRequest();
  const requestId = request?.id;

  const getPresignedUrlMutation = useMutation({
    mutationFn: async ({
      fileName,
      fileSize,
    }: {
      fileName: string;
      fileSize: number;
    }): Promise<PresignedUrlResponse> =>
      apiClient
        .query({
          fileName,
          parentId: requestId,
          parentType: ModelsEnum.REQUEST,
          size: fileSize.toString(),
          type: AttachmentCategoryEnum.ASSET,
        })
        .get("/attachments/upload-url")
        .json<PresignedUrlResponse>(),
  });

  const uploadToS3Mutation = useMutation({
    mutationFn: async ({
      fields,
      file,
      onProgress,
      presignedUrl,
    }: {
      fields: Record<string, string>;
      file: File;
      onProgress: (progress: number) => void;
      presignedUrl: string;
    }): Promise<void> => {
      const formData = new FormData();

      for (const [key, value] of Object.entries(fields)) {
        formData.append(key, value);
      }

      formData.append("file", file);

      return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);

            onProgress(progress);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.open("POST", presignedUrl);
        xhr.send(formData);
      });
    },
  });

  // Helper to update specific file
  const updateFile = useCallback(
    (fileId: string, updates: Partial<ManagedFile>) => {
      setFiles((prev) =>
        prev.map((file) =>
          file.id === fileId ? { ...file, ...updates } : file,
        ),
      );
    },
    [],
  );

  // Upload a specific file
  const uploadFile = useCallback(
    async (fileId: string): Promise<void> => {
      // Get the current file from state inside the function to avoid stale closures
      setFiles((currentFiles) => {
        const managedFile = currentFiles.find((f) => f.id === fileId);

        if (!managedFile || managedFile.status === "success")
          return currentFiles;

        // Start the upload process asynchronously
        (async () => {
          updateFile(fileId, { progress: 10, status: "uploading" });

          try {
            const presignedData = await getPresignedUrlMutation.mutateAsync({
              fileName: managedFile.file.name,
              fileSize: managedFile.file.size,
            });

            updateFile(fileId, { progress: 20 });

            await uploadToS3Mutation.mutateAsync({
              fields: presignedData.fields,
              file: managedFile.file,
              onProgress: (progress) => {
                const adjustedProgress = 20 + progress * 0.8;

                updateFile(fileId, { progress: adjustedProgress });
              },
              presignedUrl: presignedData.presignedUrl,
            });

            updateFile(fileId, {
              attachmentId: presignedData.attachmentId,
              errorMessage: undefined,
              progress: 100,
              status: "success",
            });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Upload failed";

            updateFile(fileId, {
              errorMessage,
              status: "error",
            });
            console.error(
              `Failed to upload ${managedFile.file.name}: ${errorMessage}`,
            );
          }
        })();

        return currentFiles;
      });
    },
    [updateFile, getPresignedUrlMutation, uploadToS3Mutation],
  );

  // Add new files (starts upload automatically)
  const addFiles = useCallback(
    async (newFiles: File[]): Promise<void> => {
      // Check if adding new files would exceed the limit
      if (files.length + newFiles.length > MAX_FILES) {
        toast.add({
          description: `You can only upload up to ${MAX_FILES} files at a time.`,
          title: "Too many files",
          type: "warning",
        });

        return;
      }

      const managedFiles: ManagedFile[] = newFiles.map((file) => ({
        file,
        id: nanoid(),
        progress: 0,
        status: "pending",
      }));

      setFiles((prev) => [...prev, ...managedFiles]);

      // Start uploads immediately using setTimeout to ensure state is updated
      setTimeout(() => {
        for (const managedFile of managedFiles) {
          uploadFile(managedFile.id);
        }
      }, 0);
    },
    [files.length, uploadFile],
  );

  // Add existing files (for edit mode)
  const addExistingFiles = useCallback(
    (
      existingFiles: Array<{
        attachmentId: number;
        file: File;
      }>,
    ): void => {
      const managedFiles: ManagedFile[] = existingFiles.map(
        ({ attachmentId, file }) => ({
          attachmentId,
          file,
          id: `existing-${attachmentId}`,
          isExisting: true,
          progress: 100,
          status: "success" as const,
        }),
      );

      setFiles((prev) => [...prev, ...managedFiles]);
    },
    [],
  );

  // Remove file
  const removeFile = useCallback((fileId: string): void => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  // Retry failed upload
  const retryUpload = useCallback(
    async (fileId: string): Promise<void> => {
      updateFile(fileId, {
        errorMessage: undefined,
        progress: 0,
        status: "pending",
      });
      await uploadFile(fileId);
    },
    [updateFile, uploadFile],
  );

  // Clear all files
  const clearAll = useCallback((): void => {
    setFiles([]);
  }, []);

  // Computed properties
  const isUploading = files.some((f) => f.status === "uploading");
  const successfulUploads = files.filter((f) => f.status === "success");
  const failedUploads = files.filter((f) => f.status === "error");

  return {
    // Actions
    addExistingFiles,
    addFiles,
    clearAll,
    failedUploads,
    // Computed
    files,
    hasFiles: files.length > 0,

    isUploading,
    removeFile,
    retryUpload,
    successfulUploads,
  };
}
